import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { MDBTooltip } from "mdb-react-ui-kit";
import { getTagVariantFromLabel } from "./statusUtils";

const MENU_MIN_WIDTH = 120;
const ESTIMATED_OPTION_HEIGHT = 44;

function variantToFaceModifier(variant) {
  if (variant === "success") return "active";
  if (variant === "warning") return "review";
  return "inactive";
}

function StatusFace({ label, variant, showChevron = false, className = "" }) {
  const modifier = variantToFaceModifier(variant);
  return (
    <span className={`um-status-dd-face um-status-dd-face--${modifier}${className}`}>
      <span className="um-status-dd-label">{label}</span>
      {showChevron && (
        <i className="bi bi-chevron-down um-status-chevron" aria-hidden="true" />
      )}
    </span>
  );
}

function TableStatusDropdown({
  value,
  options = [],
  getLabel = (status) => status,
  getVariant,
  onChange,
  disabled = false,
  showChevron = true,
  disabledTooltip,
  ariaLabel,
  normalizeValue = (status) => status,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: MENU_MIN_WIDTH, openUp: false });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const buttonRectRef = useRef(null);

  const currentValue = normalizeValue(value);
  const resolveVariant = (status) =>
    getVariant ? getVariant(status) : getTagVariantFromLabel(getLabel(status));

  const closeMenu = () => setIsOpen(false);

  const openMenu = () => {
    const btn = triggerRef.current;
    if (!btn?.getBoundingClientRect) {
      setIsOpen(true);
      return;
    }

    const rect = btn.getBoundingClientRect();
    buttonRectRef.current = rect;
    const menuWidth = Math.max(MENU_MIN_WIDTH, rect.width);
    let left = rect.left;
    if (left + menuWidth > window.innerWidth) {
      left = Math.max(8, window.innerWidth - menuWidth - 8);
    }
    const menuHeight = Math.min(200, options.length * ESTIMATED_OPTION_HEIGHT);
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuHeight + 12;
    const top = openUp
      ? Math.max(8, rect.top - menuHeight - 4)
      : rect.bottom + 4;

    setMenuPos({ top, left, width: menuWidth, openUp, menuHeight });
    setIsOpen(true);
  };

  const toggleMenu = () => {
    if (disabled) return;
    if (isOpen) closeMenu();
    else openMenu();
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    let rafId = null;

    const updatePosition = () => {
      const btn = triggerRef.current;
      if (!btn?.getBoundingClientRect) {
        closeMenu();
        return;
      }
      const rect = btn.getBoundingClientRect();
      buttonRectRef.current = rect;
      const menuWidth = menuPos.width || MENU_MIN_WIDTH;
      let left = rect.left;
      if (left + menuWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - menuWidth - 8);
      }
      const menuEl = menuRef.current;
      const measuredHeight = menuPos.openUp
        ? menuEl
          ? menuEl.offsetHeight || menuEl.getBoundingClientRect().height
          : menuPos.menuHeight || 80
        : 0;
      const top = menuPos.openUp
        ? Math.max(8, rect.top - measuredHeight - 4)
        : rect.bottom + 4;
      setMenuPos((prev) => ({ ...prev, top, left }));
    };

    const onScrollOrResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updatePosition);
    };

    updatePosition();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("touchmove", onScrollOrResize, { passive: true });

    const scrollableListeners = [];
    const btn = triggerRef.current;
    if (btn) {
      let el = btn.parentElement;
      while (el && el !== document.body) {
        try {
          const cs = getComputedStyle(el);
          if (cs.overflowY !== "visible" && el.scrollHeight > el.clientHeight) {
            el.addEventListener("scroll", onScrollOrResize, { passive: true });
            scrollableListeners.push(el);
          }
        } catch {
          /* ignore */
        }
        el = el.parentElement;
      }
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("touchmove", onScrollOrResize);
      scrollableListeners.forEach((el) =>
        el.removeEventListener("scroll", onScrollOrResize)
      );
    };
  }, [isOpen, menuPos.openUp, menuPos.width, menuPos.menuHeight]);

  useEffect(() => {
    if (!isOpen || !menuPos.openUp) return;
    const el = menuRef.current;
    const rect = buttonRectRef.current;
    if (!el || !rect) return;
    const measured =
      el.offsetHeight || el.getBoundingClientRect().height || menuPos.menuHeight || 0;
    const desiredTop = Math.max(8, rect.top - measured - 4);
    if (Math.abs(desiredTop - menuPos.top) > 1) {
      setMenuPos((prev) => ({ ...prev, top: desiredTop }));
    }
  }, [isOpen, menuPos.openUp, menuPos.top, menuPos.menuHeight]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (
        !event.target.closest("[data-status-dropdown]") &&
        !event.target.closest("[data-status-dropdown-fixed]")
      ) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const triggerButton = (
    <button
      type="button"
      className={`um-status-dd-trigger${isOpen ? " is-open" : ""}${
        disabled ? " um-status-dd-disabled" : ""
      }`}
      ref={triggerRef}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-expanded={isOpen}
      onClick={toggleMenu}
    >
      <StatusFace
        label={getLabel(value)}
        variant={resolveVariant(value)}
        showChevron={showChevron}
      />
    </button>
  );

  const menu =
    isOpen &&
    createPortal(
      <div
        data-status-dropdown-fixed
        ref={menuRef}
        className="um-status-dd-menu"
        style={{
          top: `${menuPos.top}px`,
          left: `${menuPos.left}px`,
          width: `${menuPos.width}px`,
        }}
      >
        {options.map((status) => {
          const isSelected = currentValue === normalizeValue(status);
          return (
            <div
              key={status}
              role="button"
              tabIndex={0}
              className={`um-status-dd-option${isSelected ? " is-selected" : ""}`}
              onClick={() => {
                if (!isSelected) onChange?.(status);
                closeMenu();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isSelected) {
                  onChange?.(status);
                  closeMenu();
                }
              }}
            >
              <StatusFace
                label={getLabel(status)}
                variant={resolveVariant(status)}
                className=" um-status-dd-face--menu"
              />
            </div>
          );
        })}
      </div>,
      document.body
    );

  if (disabled && !showChevron) {
    return (
      <div className="um-status-cell" data-status-dropdown>
        <StatusFace label={getLabel(value)} variant={resolveVariant(value)} />
      </div>
    );
  }

  if (disabled && disabledTooltip) {
    return (
      <div className="um-status-cell" data-status-dropdown>
        <MDBTooltip tag="span" title={disabledTooltip} wrapperProps={{ className: "d-inline-block" }}>
          {triggerButton}
        </MDBTooltip>
        {menu}
      </div>
    );
  }

  return (
    <div className="um-status-cell um-status-cell-dd" data-status-dropdown>
      {triggerButton}
      {menu}
    </div>
  );
}

export default TableStatusDropdown;
